import React from 'react';
import {
  Avatar,
  Button,
  Checkbox,
  Empty,
  message,
  Popconfirm,
  Select,
  Skeleton,
  Tag,
  Tooltip,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faChevronRight,
  faTrash,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  deleteTask,
  getTasks,
  PlannerPage,
  Task,
  updateTask,
} from '../../../services/tasks';
import { priorityChipBaseStyle, priorityChipStyles, tagChipStyle } from '../plannerVisuals';
import './PlannerViews.css';
import { taskReference } from '../../../utils/publicReferences';

interface ListViewProps {
  plannerPage: PlannerPage;
  filters?: Omit<NonNullable<Parameters<typeof getTasks>[0]>, 'plannerPageId'>;
  onTaskOpen?: (taskId: string) => void;
}

type SortKey = 'updatedAt' | 'dueDate' | 'priority' | 'title';
type GroupKey = 'none' | 'status' | 'assignee' | 'swimlane';
type SortDirection = 'asc' | 'desc';

const PRIORITY_ORDER: Record<Task['priority'], number> = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  URGENT: 4,
};

const ListView: React.FC<ListViewProps> = ({ plannerPage, filters = {}, onTaskOpen }) => {
  const queryClient = useQueryClient();
  const [sortKey, setSortKey] = React.useState<SortKey>('updatedAt');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('desc');
  const [groupKey, setGroupKey] = React.useState<GroupKey>('none');
  const [selectedTaskIds, setSelectedTaskIds] = React.useState<Set<string>>(new Set());

  const {
    data: tasks = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['tasks', plannerPage.id, filters],
    queryFn: () => getTasks({ plannerPageId: plannerPage.id, ...filters }),
  });

  React.useEffect(() => {
    const visibleIds = new Set(tasks.map((task) => task.id));
    setSelectedTaskIds((current) => new Set(Array.from(current).filter((id) => visibleIds.has(id))));
  }, [tasks]);

  const refreshTasks = () => queryClient.invalidateQueries({ queryKey: ['tasks', plannerPage.id] });

  const bulkMoveMutation = useMutation({
    mutationFn: async ({ taskIds, status }: { taskIds: string[]; status: string }) =>
      Promise.all(taskIds.map((id) => updateTask({ id, status }))),
    onSuccess: () => {
      message.success('Tasks moved');
      setSelectedTaskIds(new Set());
      refreshTasks();
    },
    onError: () => message.error('Could not move the selected tasks'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (taskIds: string[]) => Promise.all(taskIds.map(deleteTask)),
    onSuccess: () => {
      message.success('Tasks deleted');
      setSelectedTaskIds(new Set());
      refreshTasks();
    },
    onError: () => message.error('Could not delete the selected tasks'),
  });

  const sortedTasks = React.useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...tasks].sort((a, b) => {
      if (sortKey === 'title') return a.title.localeCompare(b.title) * direction;
      if (sortKey === 'priority') {
        return (PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]) * direction;
      }
      if (sortKey === 'dueDate') {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return (dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf()) * direction;
      }
      return (dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf()) * direction;
    });
  }, [sortDirection, sortKey, tasks]);

  const groupedTasks = React.useMemo(() => {
    if (groupKey === 'none') return [{ key: 'all', label: '', tasks: sortedTasks }];

    const groups = new Map<string, Task[]>();
    sortedTasks.forEach((task) => {
      let key = task.status;
      if (groupKey === 'assignee') key = task.assignee?.name || 'Unassigned';
      if (groupKey === 'swimlane') key = task.swimlane || 'Unassigned';
      groups.set(key, [...(groups.get(key) || []), task]);
    });

    return Array.from(groups.entries()).map(([key, groupTasks]) => ({
      key,
      label: key,
      tasks: groupTasks,
    }));
  }, [groupKey, sortedTasks]);

  const allSelected = tasks.length > 0 && selectedTaskIds.size === tasks.length;
  const someSelected = selectedTaskIds.size > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedTaskIds(allSelected ? new Set() : new Set(tasks.map((task) => task.id)));
  };

  const toggleTask = (taskId: string) => {
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const openTask = (taskId: string) => onTaskOpen?.(taskId);

  const renderTaskRow = (task: Task) => {
    const overdue = Boolean(task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day'));
    const selected = selectedTaskIds.has(task.id);
    const visibleTags = task.tags?.slice(0, 2) || [];
    const hiddenTags = Math.max((task.tags?.length || 0) - visibleTags.length, 0);

    return (
      <div
        className={`planner-list-row${selected ? ' planner-list-row--selected' : ''}`}
        key={task.id}
        role="button"
        tabIndex={0}
        onClick={() => openTask(taskReference(task))}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openTask(taskReference(task));
          }
        }}
      >
        <div className="planner-list-select" onClick={(event) => event.stopPropagation()}>
          <Checkbox
            checked={selected}
            onChange={() => toggleTask(task.id)}
            aria-label={`Select ${task.title}`}
          />
        </div>

        <div className="planner-list-title-cell">
          <div className="planner-list-title">{task.title}</div>
          {task.description && <div className="planner-list-description">{task.description}</div>}
        </div>

        <div className="planner-list-status-cell">
          <Tag bordered={false} className="planner-status-chip">{task.status}</Tag>
        </div>

        <div className="planner-list-priority-cell">
          <Tag
            bordered={false}
            style={{
              ...priorityChipBaseStyle,
              ...(priorityChipStyles[task.priority] || priorityChipStyles.MEDIUM),
              margin: 0,
            }}
          >
            {task.priority}
          </Tag>
        </div>

        <div className="planner-list-assignee-cell">
          {task.assignee ? (
            <Tooltip title={task.assignee.name}>
              <span className="planner-list-assignee">
                <Avatar size={24} src={task.assignee.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
                <span>{task.assignee.name}</span>
              </span>
            </Tooltip>
          ) : (
            <span className="planner-list-muted">Unassigned</span>
          )}
        </div>

        <div className={`planner-list-due-cell${overdue ? ' planner-list-due-cell--overdue' : ''}`}>
          {task.dueDate ? dayjs(task.dueDate).format('MMM D, YYYY') : 'No date'}
        </div>

        <div className="planner-list-tags-cell">
          {visibleTags.map((tag) => (
            <Tag key={tag} bordered={false} style={{ ...tagChipStyle, margin: 0 }}>{tag}</Tag>
          ))}
          {hiddenTags > 0 && <span className="planner-list-more-tags">+{hiddenTags}</span>}
        </div>

        <FontAwesomeIcon className="planner-list-open-icon" icon={faChevronRight} />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="planner-list-shell planner-view-loading" aria-label="Loading tasks">
        {[0, 1, 2, 3].map((item) => <Skeleton key={item} active paragraph={{ rows: 1 }} />)}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="planner-view-state">
        <div className="planner-view-state-title">Tasks could not be loaded</div>
        <div className="planner-view-state-copy">Check your connection and try again.</div>
        <Button onClick={() => refetch()}>Try again</Button>
      </div>
    );
  }

  return (
    <section className="planner-list-shell" aria-label="Task list">
      <div className="planner-view-toolbar">
        <div className="planner-view-summary">
          <span className="planner-view-count">{tasks.length}</span>
          <span>{tasks.length === 1 ? 'task' : 'tasks'}</span>
        </div>

        <div className="planner-view-toolbar-actions">
          <Select<GroupKey>
            aria-label="Group tasks"
            value={groupKey}
            onChange={setGroupKey}
            options={[
              { value: 'none', label: 'No grouping' },
              { value: 'status', label: 'Group by status' },
              { value: 'assignee', label: 'Group by assignee' },
              ...(plannerPage.swimlanes?.length ? [{ value: 'swimlane' as const, label: 'Group by swimlane' }] : []),
            ]}
            popupMatchSelectWidth={false}
          />
          <Select<SortKey>
            aria-label="Sort tasks"
            value={sortKey}
            onChange={setSortKey}
            options={[
              { value: 'updatedAt', label: 'Recently updated' },
              { value: 'dueDate', label: 'Due date' },
              { value: 'priority', label: 'Priority' },
              { value: 'title', label: 'Title' },
            ]}
            popupMatchSelectWidth={false}
          />
          <Tooltip title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}>
            <Button
              aria-label={sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'}
              icon={<FontAwesomeIcon icon={sortDirection === 'asc' ? faArrowUp : faArrowDown} />}
              onClick={() => setSortDirection((current) => current === 'asc' ? 'desc' : 'asc')}
            />
          </Tooltip>
        </div>
      </div>

      {selectedTaskIds.size > 0 && (
        <div className="planner-list-bulk-bar">
          <span>{selectedTaskIds.size} selected</span>
          <Select<string>
            aria-label="Move selected tasks"
            placeholder="Move to…"
            onChange={(status: string) => bulkMoveMutation.mutate({
              taskIds: Array.from(selectedTaskIds),
              status,
            })}
            options={plannerPage.statuses.map((status) => ({ value: status, label: status }))}
            loading={bulkMoveMutation.isPending}
          />
          <Popconfirm
            title={`Delete ${selectedTaskIds.size} selected ${selectedTaskIds.size === 1 ? 'task' : 'tasks'}?`}
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => bulkDeleteMutation.mutate(Array.from(selectedTaskIds))}
          >
            <Button danger icon={<FontAwesomeIcon icon={faTrash} />} loading={bulkDeleteMutation.isPending}>
              Delete
            </Button>
          </Popconfirm>
          <Button type="text" onClick={() => setSelectedTaskIds(new Set())}>Clear</Button>
        </div>
      )}

      {tasks.length === 0 ? (
        <div className="planner-view-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span>No tasks match the current filters.</span>}
          />
        </div>
      ) : (
        <div className="planner-list-scroll" role="region" aria-label="Planner task list" tabIndex={0}>
          <div className="planner-list-header">
            <Checkbox aria-label="Select all tasks" checked={allSelected} indeterminate={someSelected} onChange={toggleAll} />
            <span>Task</span>
            <span>Status</span>
            <span>Priority</span>
            <span>Assignee</span>
            <span>Due date</span>
            <span>Tags</span>
            <span />
          </div>

          <div className="planner-list-groups">
            {groupedTasks.map((group) => (
              <div className="planner-list-group" key={group.key}>
                {groupKey !== 'none' && (
                  <div className="planner-list-group-heading">
                    <span>{group.label}</span>
                    <span>{group.tasks.length}</span>
                  </div>
                )}
                {group.tasks.map(renderTaskRow)}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default ListView;
